import SiteShell from "../components/SiteShell";
import DiagramFlow from "../components/DiagramFlow";

const bankingCards = [
  {
    title: "Step 1: Instant Identity Verification",
    text: "Customer enters mobile number and completes secure Aadhaar/PAN verification at the kiosk.",
    image: "https://images.pexels.com/photos/5380664/pexels-photo-5380664.jpeg",
  },
  {
    title: "Step 2: Salary Profile Check",
    text: "System validates salaried profile and instantly fetches eligible personal loan limit.",
    image: "https://images.pexels.com/photos/669615/pexels-photo-669615.jpeg",
  },
  {
    title: "Step 3: Offer and Confirmation",
    text: "Approved offer is shown in real time, and the customer completes final consent in under 4 minutes.",
    image: "/team_premium_office_1772861377531.png",
  },
];

function Banking() {
  return (
    <SiteShell
      route="banking"
      heroImage="/kiosk_main_hero_1772861166220.png"
      heroHidden={true}
      highlightsHidden={true}
    >
      <section className="relative overflow-hidden bg-[#061a2e] pb-12 pt-24 text-white">
        <div className="absolute inset-0 z-0">
          <div className="absolute -left-[10%] top-0 h-full w-[40%] bg-gradient-to-r from-[#df1f2d]/20 to-transparent blur-3xl" />
          <div className="absolute -right-[10%] bottom-0 h-full w-[40%] bg-gradient-to-l from-sky-500/10 to-transparent blur-3xl" />
        </div>
        <div className="relative z-10 mx-auto max-w-[1280px] px-6 text-center md:px-10">
          <span className="inline-flex rounded-full bg-white/10 px-4 py-1.5 text-[10px] font-black uppercase tracking-[0.25em] text-sky-300 ring-1 ring-white/20 backdrop-blur-md">
            Streamlined Path
          </span>
          <h1 className="mt-8 text-4xl font-extrabold leading-tight tracking-tight md:text-7xl">
            A 3-Minute Journey <br />from Start to Approval
          </h1>
          <p className="mt-8 mx-auto max-w-3xl text-lg text-sky-100/80 leading-relaxed">
            Engineered for efficiency. Our kiosk guides salaried applicants through a high-speed journey without compromising on security or accuracy.
          </p>
          {/* <div className="mt-12 flex justify-center gap-4 text-[11px] font-black uppercase tracking-widest">
            <a href="#/company" className="rounded-full bg-white px-10 py-5 text-[#0a2c4e] shadow-2xl transition-all hover:-translate-y-1">Start Journey →</a>
            <a href="#/software" className="rounded-full border border-white/20 bg-white/5 px-10 py-5 text-white backdrop-blur-md transition-all hover:bg-white/10">Our Security</a>
          </div> */}
        </div>
      </section>

      <section className="mx-auto max-w-[1280px] px-6 pb-8 md:px-10">
        <div className="grid gap-4 rounded-3xl border border-slate-200 bg-white/80 p-4 shadow-[0_20px_50px_-12px_rgba(10,44,79,0.12)] backdrop-blur-2xl md:grid-cols-4 lg:p-6">
          {[
            { label: "Processing Speed", value: "Real-time" },
            { label: "Target Audience", value: "Salaried" },
            { label: "Security Level", value: "Level 4" },
            { label: "Uptime Guaranteed", value: "99.99%" },
          ].map((item) => (
            <article
              key={item.label}
              className="group relative rounded-2xl bg-slate-50/50 p-6 transition-all hover:bg-white hover:shadow-xl"
            >
              <div className="absolute left-0 top-1/2 h-8 w-1 -translate-y-1/2 rounded-r-full bg-[#e37921] opacity-0 transition-opacity group-hover:opacity-100" />
              <p className="text-[15px] font-black uppercase tracking-[0.2em] text-[#e37921]/80">
                {item.label}
              </p>
              <h3 className="mt-2 text-xl font-extrabold tracking-tight text-[#0a2c4f]">
                {item.value}
              </h3>
            </article>
          ))}
        </div>
      </section>

      <DiagramFlow
        title="Instant Loan Workflow"
        subtitle="Every interaction is optimized for kinetic speed, ensuring a decision is reached in record time."
        steps={[
          { title: "Smart Capture", text: "Capture mobile and OTP with zero lag for immediate session start." },
          { title: "Live Auth", text: "Real-time PAN and Aadhaar validation via encrypted endpoints." },
          { title: "Dynamic Score", text: "Instant salary validation and eligibility computation." },
          { title: "Clear Decision", text: "Transparent approval displayed with immediate next-step guidance." },
        ]}
      />

      <section className="mx-auto max-w-[1280px] px-6 py-20 md:px-10">
        <h2 className="text-3xl font-extrabold tracking-tight text-[#0a2c4f] md:text-4xl">The Kiosk Experience</h2>
        <div className="mt-12 grid gap-8 md:grid-cols-3">
          {bankingCards.map((card) => (
            <article
              key={card.title}
              className="group relative overflow-hidden rounded-[32px] border border-slate-100 bg-white p-2 shadow-xl transition-all duration-500 hover:-translate-y-2"
            >
              <div className="relative h-56 overflow-hidden rounded-[24px]">
                <img src={card.image} alt={card.title} className="h-full w-full object-cover transition duration-700 group-hover:scale-110" />
              </div>
              <div className="p-8">
                <h3 className="text-xl font-bold tracking-tight text-[#0a2c4f]">{card.title}</h3>
                <p className="mt-4 text-sm leading-relaxed text-slate-500">{card.text}</p>
              </div>
            </article>
          ))}
        </div>
      </section>

      <section className="mx-auto max-w-[1280px] px-6 pb-24 md:px-10">
        <div className="grid gap-12 overflow-hidden rounded-[40px] border border-slate-200 bg-white shadow-2xl lg:grid-cols-[0.8fr_1.2fr]">
          <div className="p-10 md:p-16">
            <p className="text-[15px] font-black uppercase tracking-[0.25em] text-[#e37921]">
              Logic Layer
            </p>
            <h2 className="mt-6 text-3xl font-extrabold tracking-tight text-[#0a2c4f]">
              Intuitive Decisioning
            </h2>
            <div className="mt-10 space-y-2">
              {[
                { label: "Input Layer", text: "Profile & salary ingestion" },
                { label: "Risk Layer", text: "KYC & compliance validation" },
                { label: "Offer Layer", text: "Personalized loan terms" }
              ].map((layer, i) => (
                <div key={layer.label}>
                  <div className="rounded-2xl border border-slate-100 bg-slate-50/50 p-6 transition-all hover:bg-white hover:shadow-lg">
                    <p className="text-[15px] font-bold uppercase tracking-widest text-[#e37921]">{layer.label}</p>
                    <p className="mt-1 font-bold text-[#17385d]">{layer.text}</p>
                  </div>
                  {i < 2 && <div className="ml-8 h-6 w-0.5 bg-slate-100 my-1" />}
                </div>
              ))}
            </div>
          </div>
          <div className="bg-slate-50 p-6 lg:p-12">
            <img
              src="/FinnCube.jpeg"
              alt="Instant loan kiosk machine"
              className="h-full w-full rounded-3xl object-cover shadow-2xl"
            />
          </div>
        </div>
      </section>
    </SiteShell >
  );
}

export default Banking;
